import { QueryClient, type QueryFunctionContext, type QueryKey } from "@tanstack/react-query";
import { API_URL } from "./constant";
import { APIError } from "./api/mutator";
import { userManager } from "./oidcClient";

/**
 * React Query is used for caching of retrieved data from the API.
 * https://tkdodo.eu/blog/effective-react-query-keys is a good guide on how to create good cache-keys.
 */
export const defaultQueryFn = async (
  { queryKey, pageParam }: QueryFunctionContext,
  access_token: string,
) => {
  // it is encoded as json because it is set using `useSessionStorage`
  const wai_org = JSON.parse(sessionStorage.getItem("wai_organization") ?? "null");
  const headers = new Headers({
    Authorization: `Bearer ${access_token}`,
    Accept: "application/json",
    ...(wai_org
      ? {
          "X-WAI-COMPANY": wai_org,
        }
      : {}),
  });

  const url = buildUri(queryKey, pageParam as string);
  const data = await fetch(url, {
    headers,
  });

  if (data.ok) {
    return data.json();
  }

  if (data.headers.get("content-type")?.includes("application/json")) {
    let details: { detail: string | undefined };
    try {
      details = await data.json();
      // oxlint-disable-next-line: eslint(no-unused-vars)
    } catch (_e) {
      // console.error(e);
      throw new Error(data.statusText);
    }
    throw new Error(details.detail);
  }
  throw new Error(data.statusText);
};

export const retryOnNonAuthError = (failureCount: number, error: Error): boolean => {
  if (error instanceof APIError && (error.status === 401 || error.status === 403)) {
    return false;
  }
  return failureCount < 3;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // Don't refetch data before 5 min has passed
      refetchOnWindowFocus: false,
      retry: retryOnNonAuthError,
      queryFn: async (options) => {
        const user = await userManager.getUser();
        if (!user?.access_token || user.expired) {
          throw new Error("Not authenticated");
        }
        return await defaultQueryFn(options, user?.access_token);
      },
    },
  },
});

const buildUri = (queryKey: QueryKey, pageParam?: string): URL => {
  const url = new URL(API_URL);

  // TODO: how to deal with the potential for `/assetgroups/<name>/assets/`
  // queryKey-wise. It does not map well to our schema, since the first key should be `/assets`
  // and we could def. be better at cache-invalidation, but that is likely not that important for our uses.
  // we could replace most of our asset/assetgroup-usage with loading parquet w/ DuckDB, which would probably be
  // much more performant.
  const filtersOrId = queryKey.at(-1);
  const method = queryKey.at(-2);
  const path = queryKey.slice(0, -2).join("/");

  if (typeof path !== "string") {
    throw new Error("Invalid queryKey");
  }

  switch (method) {
    case "list": {
      const filters = filtersOrId;
      url.pathname = `${path}/`;
      const searchParams = new URLSearchParams(filters as any);
      if (pageParam) {
        searchParams.set("page", pageParam);
      }
      url.search = searchParams.toString();
      break;
    }
    case "detail": {
      const ID = filtersOrId;
      url.pathname = `${path}/${ID}/`;
      break;
    }
    default:
      throw new Error("Invalid queryKey");
  }

  return url;
};
